<template>
  <!-- 카드 추가 모달 -->
  <UiModal
    ref="taskModalRef"
    v-model="isTaskModalOpen"
    position="right"
    :title="planNm"
    :showSizeButtons="true"
    :allowBackgroundInteraction="true"
    :allowFloating="collabTaskModalAllowFloating"
    :isScroll="true"
    showScrollbar
    modalClass="type-task"
    :data="collabTaskModalShowPlanReadNew ? taskModal?.planData || {} : {}"
    @update:modelValue="val => !val && closeTaskModal()"
    @title-icon-click="handlePlanReadAllClick"
  >
    <div class="collaboration-task-modal-body" :class="{ 'is-switching': isTaskModalSwitching }">
      <div v-if="isTaskModalSwitching" class="collaboration-task-modal-body__loading">
        <UiLoadingSpinner size="medium" :message="t('task.home.taskModal.loadingPlan')" />
      </div>
      <UiAccordionMenu
        ref="accordionMenuRef"
        :menuItems="menuItems"
        @accordion-toggle="handleAccordionToggle"
      >
      <template v-if="showActivityCompose" #header-actions-2>
        <UiButton variant="secondary-line" size="small" @click="handleDownloadActivityExcel(taskModal?.planData?.planId, planNm)">
          <span>{{ t('task.home.taskModal.exportActivity') }}</span>
        </UiButton>
        <UiButton variant="secondary" size="small" :disabled="isSavingActivity" @click="openActivityTemplateModal">{{ t('task.home.taskModal.activityTemplate') }}</UiButton>
      </template>

      <template #content-1>
        <PlanPropertyPanel
          :form="propertyForm"
          read-only
          :coop-user-list="taskModal?.coopUserList"
          :share-user-list="taskModal?.shareUserList"
        />
      </template>

      <!-- 활동 내용 섹션 -->
      <template #content-2>
        <div class="collaboration-task-modal-activity-wrap">
          <PlanActivityPanel
            :key="`${taskModal?.planData?.planId || ''}-${activityPanelRenderKey}`"
            :activities="localActivityData"
            :activity-read-only="collabTaskModalActivityReadOnly"
            :feedback-read-only="collabTaskModalFeedbackReadOnly"
            :show-activity-edit-delete="collabTaskModalShowActivityEditDelete"
            :show-compose="showActivityCompose"
            :editor-resizable="true"
            list-container-class="activity-scroll-container"
            @activity-updated="handleActivityUpdated"
          >
            <template #compose>
              <div class="activity-form-row">
                <div class="activity-form-row__date flex gap-10 items-center">
                  <p class="flex-none">{{ t('task.home.taskModal.dateLabel') }}</p>
                  <UiDatePicker :placeholder="t('task.home.taskModal.datePlaceholder')" v-model="activityDt" />
                </div>
                <div class="activity-form-row__selects">
                  <UiSelect :placeholder="t('task.home.taskModal.resultStatusPlaceholder')" v-model="resultStatusCd" :options="localizedResultStatusCdOptions" />
                  <UiSelect v-model="statusCd" :placeholder="t('task.home.taskModal.statusPlaceholder')" :options="statusCdOptions" />
                </div>
              </div>

              <TinymceEditor v-model="content_activity" :minHeight="200" :maxHeight="500" :resizable="true" :placeholder="t('task.home.taskModal.activityContentPlaceholder')" />

              <div class="activity-compose-footer">
                <div class="activity-compose-footer__attach">
                  <button class="attachment-btn action-btn" @click="isFileAttachmentModalOpen_addActivity = true">
                    <Icon name="heroicons:paper-clip" size="20" />
                    {{ attachmentFiles_addActivity.length }}
                  </button>
                </div>
                <div class="activity-compose-footer__save">
                  <UiButton variant="primary" @click="handleSaveActivity()" :disabled="isActivitySaveDisabled">{{ t('common.action.save') }}</UiButton>
                </div>
              </div>
            </template>

            <template #list-footer>
              <div v-if="infiniteScroll.isLoading.value" class="loading-indicator">
                <UiLoadingSpinner size="small" :message="t('task.home.taskModal.loadingActivities')" />
              </div>
              <div v-if="localActivityData.length > 0 && !infiniteScroll.hasMoreData.value" class="no-more-data">
                {{ t('task.home.taskModal.allActivitiesLoaded') }}
              </div>
            </template>
          </PlanActivityPanel>
        </div>
      </template>

      <template #content-3>
        <PlanLogPanel :logs="avtLogList" :loading="isAvtLogLoading" />
      </template>
    </UiAccordionMenu>
    </div>
  </UiModal>

  <!-- 활동 추가 첨부파일 모달 -->
  <UiModal
    v-model="isFileAttachmentModalOpen_addActivity"
    :title="t('common.attachments')"
    size="medium"
    :show-footer="true"
    teleport-to-body
    :height="480"
    :z-index="1100"
  >
    <!-- 작성/수정 화면 (업로드 가능) -->
    <UiFileAttachment v-model="attachmentFiles_addActivity" />
    <template #footerActions>
      <UiButton variant="secondary" @click="cancelAttachment_addActivity">{{ t('common.action.cancel') }}</UiButton>
      <UiButton variant="primary" @click="saveAttachments_addActivity">{{ t('common.action.attachDone') }}</UiButton>
    </template>
  </UiModal>

  <!-- 활동 첨부파일 조회 모달 -->
  <UiModal
    v-model="isFileAttachmentModalOpen_detailActivity"
    :title="t('common.attachments')"
    size="medium"
    :show-footer="true"
    teleport-to-body
    :height="480"
    :z-index="1100"
  >
    <UiFileAttachment v-model="attachmentFiles_detailActivity" :isFileUploadDisabled="true" />
  </UiModal>

  <!-- 활동 수정모드 첨부파일 모달 -->
  <UiModal
    v-model="isFileAttachmentModalOpen_editActivity"
    :title="t('common.attachments')"
    size="medium"
    :show-footer="true"
    teleport-to-body
    :height="480"
    :z-index="1100"
  >
    <!-- 작성/수정 화면 (업로드 가능) -->
    <UiFileAttachment v-model="attachmentFiles_editActivity" />
    <template #footerActions>
      <UiButton variant="secondary" @click="cancelAttachment_editActivity">{{ t('common.action.cancel') }}</UiButton>
      <UiButton variant="primary" @click="saveAttachments_editActivity">{{ t('common.action.attachDone') }}</UiButton>
    </template>
  </UiModal>

  <!-- 피드백/답글 첨부파일 모달 (활동 추가와 동일) -->
  <UiModal
    v-model="isFileAttachmentModalOpen_feedback"
    :title="t('common.attachments')"
    size="medium"
    :show-footer="true"
    teleport-to-body
    :height="480"
    :z-index="1100"
  >
    <UiFileAttachment v-model="attachmentFiles_feedback" />
    <template #footerActions>
      <UiButton variant="secondary" @click="cancelFeedbackAttachmentModal">{{ t('common.action.cancel') }}</UiButton>
      <UiButton variant="primary" @click="confirmFeedbackAttachmentModal">{{ t('common.action.attachDone') }}</UiButton>
    </template>
  </UiModal>

  <UiModal
    v-model="isActivityTemplateModalOpen"
    :title="t('task.home.taskModal.activityTemplate')"
    size="xlarge"
    :show-footer="true"
    :show-size-buttons="false"
    :is-scroll="true"
    :z-index="2999"
  >
    <div class="activity-template-modal">
      <div class="activity-template-modal__filters">
        <div class="flex items-center gap-8">
          <span class="activity-template-modal__filter-label">{{ t('task.home.taskModal.template.activityType') }}</span>
          <UiSelect v-model="activityTemplateFind.findActivityCategoryId" :options="activityTemplateCategoryOptions" :placeholder="t('common.action.all')" width="160px" />
        </div>
        <label class="activity-template-modal__favorite-filter">
          <UiCheckbox v-model="activityTemplateFind.searchImportantOnly" id="home-activity-template-favorites-only" label="" />
          <span>{{ t('task.home.taskModal.template.favorites') }}</span>
        </label>
      </div>

      <div class="activity-template-modal__content">
        <article class="activity-template-modal__left">
          <div class="ui-table-wrapper-default activity-template-modal__table-wrap">
            <table class="ui-table-default">
              <colgroup>
                <col style="width: 20%" />
                <col style="width: 32%" />
                <col style="width: 28%" />
                <col style="width: 10%" />
                <col style="width: 10%" />
              </colgroup>
              <thead class="ui-table-default__header">
                <tr>
                  <th>{{ t('task.home.taskModal.template.col.category') }}</th>
                  <th>{{ t('task.home.taskModal.template.col.name') }}</th>
                  <th>{{ t('task.home.taskModal.template.col.note') }}</th>
                  <th>{{ t('task.home.taskModal.template.favorites') }}</th>
                  <th>{{ t('common.action.select') }}</th>
                </tr>
              </thead>
              <tbody class="ui-table-default__body">
                <template v-if="activityTemplateRows.length === 0">
                  <tr>
                    <td colspan="5" class="text-center">{{ t('common.noData') }}</td>
                  </tr>
                </template>
                <template v-else>
                  <tr
                    v-for="row in activityTemplateRows"
                    :key="row.activityTemplateId"
                    class="activity-template-modal__row"
                    :class="{ 'is-selected': activityTemplateFind.findActTemplateId === row.activityTemplateId }"
                    @click="handleSelectActivityTemplateDetail(row)"
                  >
                    <td class="txt-c" style="text-align:center;">{{ row.activityCategoryNm }}</td>
                    <td class="txt-l" style="text-align:center;">{{ row.activityTemplateNm }}</td>
                    <td class="txt-l" style="text-align:center;">{{ row.description }}</td>
                    <td class="txt-c" style="text-align:center;">
                      <button type="button" class="activity-template-modal__favorite-btn" @click.stop="handleToggleActivityTemplateFavorite(row)">
                        <i class="icon icon-md" :class="row.importYn === 'Y' ? 'icon-star-yellow' : 'icon-star'"></i>
                      </button>
                    </td>
                    <td class="txt-c" style="text-align:center;">
                      <input
                        v-model="activityTemplateFind.findActTemplateId"
                        type="radio"
                        name="home-activity-template-select"
                        :value="row.activityTemplateId"
                        @change="handleChangeActivityTemplate(row)"
                      />
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </article>

        <article class="activity-template-modal__right">
          <ul class="activity-template-modal__meta">
            <li>{{ t('task.home.taskModal.template.meta.taskType') }} {{ activityTemplateFind.findActCateNm || '-' }}</li>
            <li>{{ t('task.home.taskModal.template.meta.boardName') }} {{ activityTemplateFind.findActTemplateNm || '-' }}</li>
          </ul>
          <div class="activity-template-modal__preview-wrap">
            <TinymceViewer :content="activityTemplateFind.content" />
          </div>
        </article>
      </div>
    </div>
    <template #footerActions>
      <div class="flex justify-end gap-8 w-full">
        <UiButton variant="secondary" @click="isActivityTemplateModalOpen = false">{{ t('common.action.close') }}</UiButton>
        <UiButton variant="primary" @click="handleApplyActivityTemplate">{{ t('task.home.taskModal.template.apply') }}</UiButton>
      </div>
    </template>
  </UiModal>

  <UiModal
    v-if="isTaskModalOpen"
    v-model="isPdfDateModalOpen"
    :title="t('task.board.taskModal.pdfExport.title')"
    size="small"
    :show-footer="true"
    :show-size-buttons="false"
    :z-index="99999"
  >
    <div class="pdf-export-modal__body">
      <UiTabs
        v-model="activityExportTabId"
        defaultTab="excel"
        :width="'full'"
        :tabs="activityExportTabs"
        class="pdf-export-modal__tabs"
      >
        <template #excel />
        <template #pdf />
      </UiTabs>
      <div class="pdf-export-modal__tab-panel">
        <p class="pdf-export-modal__message">{{ t('task.board.confirm.exportActivity.message') }}</p>
        <div class="pdf-export-modal__period">
          <span class="pdf-export-modal__period-label">{{ t('task.board.taskModal.pdfExport.period') }}</span>
          <UiDatePicker
            v-model="pdfExportDateRange"
            isRange
            splitRange
            :startPlaceholder="t('common.datePicker.startDate')"
            :endPlaceholder="t('common.datePicker.endDate')"
          />
        </div>
      </div>
    </div>
    <template #footerActions>
      <div class="flex justify-end gap-8 w-full">
        <UiButton variant="secondary" @click="closePdfDateModal">{{ t('common.action.cancel') }}</UiButton>
        <UiButton variant="primary" @click="handleConfirmPdfDownload">{{ t('task.board.taskModal.pdfExport.download') }}</UiButton>
      </div>
    </template>
  </UiModal>
</template>

<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useCollaborationStore } from '~/composables/useCollaborationStore';
import { useCollaborationApi } from '~/composables/useCollaborationApi';
import { showAlert } from '~/composables/useConfirmDialog';
import {
  confirmActivityTemplateApply,
  mergeActivityTemplateContent,
} from '~/composables/domain/activity/useActivityTemplateApply';
import { useCommon } from '~/composables/useCommon';
import { useAttachmentApi } from '~/composables/attachment/useAttachmentApi';
import { useInfiniteScroll } from '~/composables/useInfiniteScroll';
import UiFileAttachment from '~/components/attachment/UiFileAttachment.vue';
import UiLoadingSpinner from '~/components/common/UI/UiLoadingSpinner.vue';
import TinymceViewer from '~/components/common/UI/TinymceViewer.vue';
import PlanPropertyPanel from '~/components/domain/Plan/PlanPropertyPanel.vue';
import PlanActivityPanel from '~/components/domain/Plan/PlanActivityPanel.vue';
import PlanLogPanel from '~/components/domain/Plan/PlanLogPanel.vue';
import { mapPlanToPropertyForm } from '~/composables/domain/plan/mapPlanToPropertyForm';
import { useTaskFilterI18n } from '~/composables/task/useTaskFilterI18n';

const { t } = useI18n();
const { appendFileToFormData } = useAttachmentApi();
const { isEmpty } = useCommon();

const taskModalRef = ref(null);
const accordionMenuRef = ref(null);

/** 피드백 저장·활동 목록 갱신 후 UiModal 변경 감지 baseline 재설정 */
const scheduleCaptureInitialTextValues = () => {
  nextTick(() => {
    taskModalRef.value?.captureInitialTextValues?.();
    setTimeout(() => {
      taskModalRef.value?.captureInitialTextValues?.();
    }, 800);
  });
};

const {
  isTaskModalOpen,
  taskModal,
  collabTaskModalActivityReadOnly,
  collabTaskModalFeedbackReadOnly,
  collabTaskModalHideLog,
  collabTaskModalAllowFloating,
  collabTaskModalShowActivityEditDelete,
  collabTaskModalShowPlanReadNew,
  isTaskModalSwitching,
  setTaskModalAccordionTab,
  getDefaultTaskModalMenuItems,
  activities_home,
  homeStatus,
  findYear,
  resultStatusCdOptions,
  statusCdOptions,
  isFileAttachmentModalOpen_detailActivity,
  attachmentFiles_detailActivity,
  isFileAttachmentModalOpen_editActivity,
  attachmentFiles_editActivity,
  isFileAttachmentModalOpen_feedback,
  attachmentFiles_feedback,
  confirmFeedbackAttachmentModal,
  cancelFeedbackAttachmentModal,
  homeTaskModalClear,
  handlePlanSearch,
  handleDownloadActivityExcel,
  isPdfDateModalOpen,
  pdfExportDateRange,
  pdfExportPlanId,
  activityExportTabId,
  closePdfDateModal,
  handleConfirmPdfDownload,
  handleFetchHomeActivityListAccumulated,
  homeModalActivityLoadedPage,
  homeModalActivityHasMore,
  focusActivity,
  focusActivityScrollOnly,
  avtLogList,
  handleFetchAvtLogList,
  isEditorEmpty,
  handleReadArticle,
} = useCollaborationStore();

const { localizeResultStatusOptions } = useTaskFilterI18n();
const localizedResultStatusCdOptions = localizeResultStatusOptions(resultStatusCdOptions);

const { saveActivity, fetchSelectAvtCategoryList, fetchSelectActivityTmplList, fetchUpdateImportAvt } =
  useCollaborationApi();

const activityExportTabs = computed(() => [
  { id: 'excel', label: t('task.board.taskModal.pdfExport.tabExcel'), slot: 'excel' },
  { id: 'pdf', label: t('task.board.taskModal.pdfExport.tabPdf'), slot: 'pdf' },
]);

// Props
const props = defineProps({});

const resultStatusCd = ref('');
const statusCd = ref('');
const content_activity = ref('');
const activityDt = ref('');

const isAvtLogLoading = ref(false);
const isSavingActivity = ref(false);
const isActivityTemplateModalOpen = ref(false);
/** 활동 탭 표시 시 TinymceViewer(iframe) 재마운트 — 계획 탭만 연 채 데이터 교체 후 본문 깨짐 방지 */
const activityPanelRenderKey = ref(0);
const localActivityData = ref([]);

// 무한 스크롤 설정 (TaskModal.vue와 동일 패턴)
const infiniteScroll = useInfiniteScroll(
  async page => {
    const planId = taskModal.value?.planData?.planId;
    if (!planId) {
      return { hasMore: false, data: [] };
    }

    const { items, rawPageLength } = await handleFetchHomeActivityListAccumulated(
      planId,
      page,
      taskModal.value?.planData,
    );

    localActivityData.value = items;

    const hasMore = rawPageLength >= 50;

    return {
      hasMore,
      data: items,
    };
  },
  {
    initialPage: 1,
    rowsPerPage: 50,
    scrollMargin: 10,
  },
);
const isActivitySaveDisabled = computed(
  () => isSavingActivity.value || isEditorEmpty(content_activity.value),
);

// activities_home(초기 1페이지) 변경 시 로컬 상태·무한 스크롤 초기화 — TaskModal props.activityData watch와 동일
watch(
  () => activities_home.value,
  newActivityData => {
    if (newActivityData) {
      localActivityData.value = newActivityData;
      infiniteScroll.reset();
      infiniteScroll.currentPage.value = homeModalActivityLoadedPage.value;
      infiniteScroll.hasMoreData.value = homeModalActivityHasMore.value;
    }
  },
  { immediate: true, deep: true },
);

// 모달 body 스크롤 이벤트 감지 — TaskModal.vue와 동일
let modalBodyElement = null;

watch(
  () => isTaskModalOpen.value,
  async isOpen => {
    if (isOpen) {
      await nextTick();
      const modalBodies = document.querySelectorAll('.ui-popup__body');
      if (modalBodies.length > 0) {
        modalBodyElement = modalBodies[modalBodies.length - 1];
        modalBodyElement.addEventListener('scroll', infiniteScroll.handleScroll);
      }
    } else if (modalBodyElement) {
      modalBodyElement.removeEventListener('scroll', infiniteScroll.handleScroll);
      modalBodyElement = null;
    }
  },
  { immediate: true },
);

// 활동 등록 첨부파일 모달
const isFileAttachmentModalOpen_addActivity = ref(false);
const attachmentFiles_addActivity = ref([]);

const activityTemplateFind = ref({
  findActivityCategoryId: '',
  findActCateNm: '',
  findActCateId: '',
  findActTemplateNm: '',
  findActTemplateId: '',
  content: '',
  searchImportantOnly: false,
});
const TASK_MODAL_TAB_KEYS = {
  plan: 'plan',
  activity: 'activity',
  log: 'log',
};

const activityTemplateCategoryOptions = ref([{ value: '', label: '' }]);
const activityTemplateRows = ref([]);

// 첨부파일 저장
const saveAttachments_addActivity = () => {
  isFileAttachmentModalOpen_addActivity.value = false;
};

// 첨부파일 취소
const cancelAttachment_addActivity = () => {
  isFileAttachmentModalOpen_addActivity.value = false;
  attachmentFiles_addActivity.value = [];
};

// 첨부파일(활동 수정모드드) 저장
const saveAttachments_editActivity = () => {
  isFileAttachmentModalOpen_editActivity.value = false;
};

// 첨부파일(활동 수정모드) 취소
const cancelAttachment_editActivity = () => {
  isFileAttachmentModalOpen_editActivity.value = false;
};

// 메뉴 설정 — 마지막 선택 탭 유지
const menuItems = computed(() => {
  const items = getDefaultTaskModalMenuItems(collabTaskModalHideLog.value);
  return items.map(item => {
    if (item.tabKey) {
      return {
        ...item,
        title: t(`task.home.taskModal.tab.${item.tabKey}`),
      };
    }
    return item;
  });
});

const planNm = computed(() => taskModal.value?.planData?.planNm || '');

const propertyForm = computed(() =>
  mapPlanToPropertyForm(taskModal.value?.planData, {
    coopUserList: taskModal.value?.coopUserList,
    shareUserList: taskModal.value?.shareUserList,
  }),
);

const showActivityCompose = computed(
  () => !collabTaskModalActivityReadOnly.value && taskModal.value?.planData?.planType !== 'S',
);

const handleAccordionToggle = async ({ index, isOpen }) => {
  const menuItem = menuItems.value[index];
  const planId = taskModal.value?.planData?.planId;

  if (isOpen && menuItem?.tabKey) {
    setTaskModalAccordionTab(menuItem.tabKey);
  }

  if (isOpen && menuItem?.tabKey === TASK_MODAL_TAB_KEYS.activity) {
    activityPanelRenderKey.value += 1;
    await nextTick();
  }

  if (!isOpen || menuItem?.tabKey !== TASK_MODAL_TAB_KEYS.log || !planId) return;

  isAvtLogLoading.value = true;
  try {
    await handleFetchAvtLogList(planId);
  } finally {
    isAvtLogLoading.value = false;
  }
};

const openActivityAccordion = async () => {
  setTaskModalAccordionTab(TASK_MODAL_TAB_KEYS.activity);
  const index = menuItems.value.findIndex(item => item.tabKey === TASK_MODAL_TAB_KEYS.activity);
  if (index < 0) return;
  await nextTick();
  accordionMenuRef.value?.openAccordion(index);
};

const handleSaveActivity = async () => {
  if (isSavingActivity.value) return;

  if (resultStatusCd.value == '' || resultStatusCd.value == null || resultStatusCd.value == undefined) {
    showAlert(t('common.error'), t('task.home.taskModal.alert.selectResultStatus'));
    return;
  }

  if (statusCd.value == '' || statusCd.value == null || statusCd.value == undefined) {
    showAlert(t('common.error'), t('task.home.taskModal.alert.selectStatus'));
    return;
  }

  const formData = new FormData();

  // 업로드 첨부파일 추가
  if (attachmentFiles_addActivity.value && attachmentFiles_addActivity.value.length > 0) {
    appendFileToFormData({
      formData,
      files: attachmentFiles_addActivity.value, // 전체 파일
      deleteFileIds: [], // 체크된 삭제 대상 fileSn
      fileMeta: {
        inputFileNames: ['upload' + new Date().getTime()],
        atchFileIds: activities_home?.value?.atchFileId ? [activities_home?.value?.atchFileId] : ['NULL'],
      },
    });
  }

  const params = {
    findYear: findYear.value,
    findBoardId: homeStatus.value.boardId,
    boardId: homeStatus.value.boardId,
    planId: taskModal.value?.planData?.planId,
    content: content_activity.value,
    activityDt: formatDate_yyyymmdd(activityDt.value),
    statusCd: statusCd.value,
    resultStatusCd: resultStatusCd.value,
    findDtGubun: '02',
    maxMon: 12,
    findAvtPlanStatus: '',
    findDay: '',
    findImportant: 'N',
    findResultStatusCd: '',
    findStatusCd: '',
    findCoopShareUserId: '',
  };

  Object.keys(params).forEach(key => {
    formData.append(key, params[key]);
  });

  isSavingActivity.value = true;
  try {
    await saveActivity(formData);
    showAlert(t('common.success'), t('task.home.taskModal.alert.activitySaved'));

    // 활동 목록 먼저 갱신 (homeTaskModalClear 내부에서 handleActivitySearch(true) 호출)
    await homeTaskModalClear();

    // 업무 목록 리로드 (활동 목록은 유지하기 위해 clearActivityList: false)
    await handlePlanSearch('N', false);
  } finally {
    isSavingActivity.value = false;
  }
};

// 날짜를 yyyymmdd 형태로 변환
const formatDate_yyyymmdd = date => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const handleLoadActivityTemplateCategoryList = async () => {
  const res = await fetchSelectAvtCategoryList();
  const list = res?.avtCategoryList ?? res?.dataList ?? [];
  activityTemplateCategoryOptions.value = [
    { value: '', label: t('common.action.all') },
    ...list.map(item => ({
      value: item.activityCategoryId ?? item.activityCategoryNm ?? '',
      label: item.activityCategoryNm ?? item.activityCategoryId ?? '',
    })),
  ];
};

const handleSelectActivityTemplateList = async () => {
  const params = new URLSearchParams();
  params.append('findActivityCategoryId', activityTemplateFind.value.findActivityCategoryId || '');
  params.append('searchImportYn', activityTemplateFind.value.searchImportantOnly ? 'Y' : 'N');
  const res = await fetchSelectActivityTmplList(params);
  const list = res?.avtCategoryList ?? res?.dataList ?? [];
  activityTemplateRows.value = Array.isArray(list) ? list : [];

  if (activityTemplateRows.value.length > 0) {
    handleSelectActivityTemplateDetail(activityTemplateRows.value[0]);
  } else {
    activityTemplateFind.value.findActTemplateId = '';
    activityTemplateFind.value.findActTemplateNm = '';
    activityTemplateFind.value.findActCateId = '';
    activityTemplateFind.value.findActCateNm = '';
    activityTemplateFind.value.content = '';
  }
};

const handleSelectActivityTemplateDetail = row => {
  if (!row) return;
  activityTemplateFind.value.findActTemplateId = row.activityTemplateId || '';
  activityTemplateFind.value.findActTemplateNm = row.activityTemplateNm || '';
  activityTemplateFind.value.findActCateId = row.activityCategoryId || '';
  activityTemplateFind.value.findActCateNm = row.activityCategoryNm || '';
  activityTemplateFind.value.content = normalizeTemplateTableBorder(row.content || '');
};

const handleChangeActivityTemplate = row => {
  handleSelectActivityTemplateDetail(row);
};

const handleToggleActivityTemplateFavorite = async row => {
  const params = new URLSearchParams();
  params.append('activityTemplateId', row.activityTemplateId);
  params.append('importYn', row.importYn === 'Y' ? 'N' : 'Y');
  await fetchUpdateImportAvt(params);
  row.importYn = row.importYn === 'Y' ? 'N' : 'Y';
};

const normalizeTemplateTableBorder = html => {
  if (!html) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;

  root.querySelectorAll('table[border]').forEach(table => {
    if (String(table.getAttribute('border') || '').trim() === '0') {
      table.setAttribute('border', '0');
      table.setAttribute('data-tinymce-table-border', 'guide');
      table.querySelectorAll('td, th').forEach(cell => {
        cell.style.removeProperty('border');
        cell.style.removeProperty('border-width');
        cell.style.removeProperty('border-style');
        cell.style.removeProperty('border-color');
        cell.style.removeProperty('border-top-style');
        cell.style.removeProperty('border-right-style');
        cell.style.removeProperty('border-bottom-style');
        cell.style.removeProperty('border-left-style');
      });
    }
  });

  return root.innerHTML;
};

const openActivityTemplateModal = async () => {
  await handleLoadActivityTemplateCategoryList();
  await handleSelectActivityTemplateList();
  isActivityTemplateModalOpen.value = true;
};

const handleApplyActivityTemplate = () => {
  if (!activityTemplateFind.value.findActTemplateId) {
    showAlert(t('common.notice'), t('task.home.taskModal.alert.selectTemplate'));
    return;
  }

  const templateContent = normalizeTemplateTableBorder(activityTemplateFind.value.content || '');

  confirmActivityTemplateApply({
    currentContent: content_activity.value,
    onApply: async replaceExisting => {
      content_activity.value = mergeActivityTemplateContent(
        content_activity.value,
        templateContent,
        replaceExisting,
      );
      isActivityTemplateModalOpen.value = false;
      await openActivityAccordion();
    },
  });
};

// 모달 닫기
const closeTaskModal = () => {
  homeStatus.value.planId = '';
  isTaskModalOpen.value = false;
  isTaskModalSwitching.value = false;
  collabTaskModalActivityReadOnly.value = false;
  collabTaskModalFeedbackReadOnly.value = false;
  collabTaskModalHideLog.value = false;
  collabTaskModalShowActivityEditDelete.value = false;
  collabTaskModalShowPlanReadNew.value = false;
  activityPanelRenderKey.value = 0;
  localActivityData.value = [];
  infiniteScroll.reset();
  focusActivity.value = '';
  focusActivityScrollOnly.value = false;
  homeModalActivityLoadedPage.value = 1;
  homeModalActivityHasMore.value = true;
  taskModal.value = {};
  avtLogList.value = [];
  clearTaskForm();
};

/** 모달 내 활동/피드백/답글 unread 카운트 초기화 */
const clearUnreadInActivityTree = list => {
  if (!Array.isArray(list)) return;
  list.forEach(activity => {
    activity.activityUnreadCnt = 0;
    (activity.feedbackList || []).forEach(feedback => {
      feedback.activityUnreadCnt = 0;
      (feedback.replyList || []).forEach(reply => {
        reply.activityUnreadCnt = 0;
      });
    });
  });
};

/** title NEW 클릭 — plan 단위 모두 읽음 */
const handlePlanReadAllClick = async () => {
  const planData = taskModal.value?.planData;
  const planId = planData?.planId;
  const boardId = planData?.boardId || homeStatus.value.boardId;
  if (!planId || !boardId) return;

  await handleReadArticle({
    readUnit: 'plan',
    boardId,
    planId,
  });

  if (planData) {
    planData.activityUnreadCnt = 0;
  }
  clearUnreadInActivityTree(localActivityData.value);
  clearUnreadInActivityTree(activities_home.value);
};

// 카드 폼 초기화
const clearTaskForm = () => {};

// 활동 업데이트 핸들러 — 팝업 내부 목록만 갱신 (배경 피드/사이드바는 ActivityCard.syncHomeAfterFeedbackChange)
const handleActivityUpdated = activityItems => {
  activities_home.value = activityItems;
  localActivityData.value = activityItems;
  infiniteScroll.reset();
  scheduleCaptureInitialTextValues();
};

watch(
  () => activityTemplateFind.value.findActivityCategoryId,
  async () => {
    if (!isActivityTemplateModalOpen.value) return;
    await handleSelectActivityTemplateList();
  },
);

watch(
  () => activityTemplateFind.value.searchImportantOnly,
  async () => {
    if (!isActivityTemplateModalOpen.value) return;
    await handleSelectActivityTemplateList();
  },
);

// 메뉴 이동 시 모달 닫기
const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    if (isTaskModalOpen.value) {
      closeTaskModal();
    }
  },
);

// 모달이 열릴 때 content_activity 초기화 및 디폴트값 설정
watch(
  () => isTaskModalOpen.value,
  isOpen => {
    if (isOpen) {
      // 활동일자 현재일자로 기본 세팅
      activityDt.value = new Date().toISOString().slice(0, 10).replace(/-/g, '.');

      content_activity.value = '';
      // 디폴트값 재설정 (옵션이 비동기로 로드될 수 있으므로)
      if (resultStatusCdOptions.value && resultStatusCdOptions.value.length > 0) {
        resultStatusCd.value = resultStatusCdOptions.value[0].value;
      }
      if (statusCdOptions.value && statusCdOptions.value.length > 0) {
        // 진행상태/상황은 임시로 2번째 옵션을 선택
        statusCd.value = statusCdOptions.value[1].value;
      }
    }
  },
);
</script>

<style scoped lang="scss">
.collaboration-task-modal-body {
  position: relative;
  min-height: 120px;

  &.is-switching {
    pointer-events: none;
    min-height: 240px;
  }

  &__loading {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.88);
  }
}

.collaboration-task-modal-activity-wrap {
  position: relative;
  min-height: 160px;
}

.collaboration-task-modal-activity-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  /* 목록 전체 높이 기준 세로 중앙(align-items:center)으로 두면 목록이 길 때 스피너가 화면 밖에 그려짐 → 상단 정렬 */
  align-items: flex-start;
  justify-content: center;
  background: rgba(255, 255, 255, 0.88);
  pointer-events: none;
}

/* 목록이 아무리 길어도 스피너가 보이는 스크롤 영역(뷰포트) 안에 머무르도록 상단에 sticky 고정 */
.collaboration-task-modal-activity-loading :deep(.ui-loading-spinner) {
  position: sticky;
  top: 0;
  padding-top: 120px;
}

:deep(.ui-popup.type-task) {
  .ui-popup__content.ui-popup--right {
    box-sizing: border-box;
    overflow: hidden;
  }

  .ui-popup__header,
  .ui-popup__body,
  .ui-popup__footer {
    box-sizing: border-box;
    padding: 16px;
  }

  .ui-popup__body {
    min-height: 0;
    flex: 1 1 auto;
    overflow-y: auto;
  }
}

@media (max-width: 1024px) {
  :deep(.ui-popup.type-task) {
    .ui-popup__content.ui-popup--right {
      max-height: 100dvh;
    }
  }
}

@media (max-width: 768px) {
  :deep(.ui-popup.type-task) {
    .ui-popup__content.ui-popup--right {
      margin: 12px;
      max-height: calc(100vh - 24px);
      max-height: calc(100dvh - 24px);
    }

    .ui-popup--fullscreen {
      position: fixed;
      top: 12px;
      left: 12px;
      right: 12px;
      bottom: 12px;
      margin: 0;
      border-radius: 8px;
      width: auto;
      height: auto;
      max-width: none;
      max-height: none;
    }

    .ui-popup--right {
      position: fixed;
      top: 12px;
      left: 12px;
      right: 12px;
      bottom: 12px;
      margin: 0;
      border-radius: 8px;
      min-width: auto;
      width: auto;
      height: auto;
      max-width: none;
      max-height: none;
    }
  }
}

.activity-form-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin-bottom: 16px;
  min-width: 0;
}

.activity-form-row__date {
  flex: 0 0 auto;
  min-width: 0;

  :deep(.ui-datepicker) {
    width: 148px;
    min-width: 148px;
    flex-shrink: 0;
  }
}

.activity-form-row__selects {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex: 1 1 200px;
  min-width: 0;
  margin-left: auto;
}

.activity-form-row__selects :deep(.ui-select) {
  flex: 1 1 160px;
  min-width: 0 !important;
  max-width: 200px;
  width: 100% !important;
}

.activity-compose-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.activity-compose-footer__save {
  display: flex;
  align-items: center;
}

.activity-compose-footer__attach {
  display: flex;
  align-items: center;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: #666;
}

.no-more-data {
  text-align: center;
  color: #999;
  font-size: 14px;
  padding-bottom: 10px;
}

.attachment-btn.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: #374151;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: #0084ff;
  }
}

.activity-template-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.activity-template-modal__filters {
  display: flex;
  align-items: center;
  gap: 16px;
}

.activity-template-modal__filter-label {
  font-size: 14px;
  color: #111;
}

.activity-template-modal__favorite-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.activity-template-modal__content {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(260px, 1fr);
  gap: 16px;
}

.activity-template-modal__table-wrap {
  max-height: 420px;
  overflow: auto;
}

.activity-template-modal__row {
  cursor: pointer;
}

.activity-template-modal__row.is-selected {
  background-color: #f7fbff;
}

.activity-template-modal__favorite-btn {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.activity-template-modal__right {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  padding: 12px;
}

.activity-template-modal__meta {
  margin: 0;
  padding-left: 14px;
  font-size: 13px;
  line-height: 1.7;
  color: #111;
}

.activity-template-modal__preview-wrap {
  margin-top: 12px;
  border: 1px solid #e5e7eb;
  padding: 8px;
  min-height: 120px;
  max-height: 380px;
  overflow: auto;
}

.activity-template-modal__preview-wrap :deep(.tinymce-viewer__frame) {
  min-height: 320px;
}

.pdf-export-modal__body {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 0;
  min-height: 180px;
  padding: 16px 20px 24px;
  text-align: center;
}

.pdf-export-modal__tabs {
  width: 100%;

  :deep(.ui-tabs__content) {
    display: none;
  }
}

.pdf-export-modal__tab-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px 0 4px;
  text-align: center;
}

.pdf-export-modal__message {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: var(--color-gray-70, #464c53);
  text-align: center;
  word-break: keep-all;
}

.pdf-export-modal__period {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
}

.pdf-export-modal__period-label {
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-gray-80, #2f343a);
  line-height: 1;
}
</style>
